/**
 * Sample data for freshly dropped widget nodes, so a chart dragged from the
 * palette renders as a real chart instead of an empty box.
 *
 * ## Why not faker.js
 *
 * faker is the wrong tool here, for three reasons:
 *
 * 1. **Shape, not values, is the hard part.** Each shared component wants a
 *    specific `[data]` envelope (`capabilities` + `staticData`, `waterfallBars`,
 *    `headers`, …). faker generates names and addresses; it knows nothing about
 *    those envelopes, so we would still hand-write every wrapper below and use
 *    faker only for the numbers inside — the easy 5%.
 * 2. **Weight.** `@faker-js/faker` is ~600 KB for what amounts to a few dozen
 *    category labels and some random integers.
 * 3. **Determinism.** A design must look the same every time it is opened. The
 *    seeded generator below always produces the same series for the same node
 *    id, so reopening a design never silently changes the picture.
 *
 * So: a small seeded PRNG plus domain-shaped label pools. Everything here is
 * pure and Angular-free.
 *
 * Sample data is marked with `sample: true` on the widget, so the inspector can
 * say so and the publish payload can strip it — it must never be mistaken for
 * real bound data.
 */

import type { WidgetContent } from '../design-builder.types';

// ── Deterministic pseudo-random ──────────────────────────────────────────────

/** Mulberry32 — tiny, fast, good enough for sample series. */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable numeric seed from a node id, so each widget gets its own series. */
function seedFrom(key: string): number {
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

// ── Label pools ──────────────────────────────────────────────────────────────

const REGIONS = ['North', 'South', 'East', 'West', 'Central'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SEGMENTS = ['Enterprise', 'Mid-market', 'SMB'];
const PRODUCTS = ['Alpha', 'Bravo', 'Cassia', 'Delta', 'Echo', 'Foxtrot'];

export const SAMPLE_CATEGORY_FIELD = 'category';
export const SAMPLE_VALUE_FIELD = 'revenue';
export const SAMPLE_SECOND_VALUE_FIELD = 'cost';
export const SAMPLE_SERIES_FIELD = 'segment';

/** One flat rows table every row-shaped widget can read. */
export interface SampleRowsOptions {
  categories?: readonly string[];
  /** Split into multiple series (grouped/stacked/multi-line). */
  series?: readonly string[];
  min?: number;
  max?: number;
}

export function sampleRows(
  seedKey: string,
  { categories = REGIONS, series, min = 20000, max = 140000 }: SampleRowsOptions = {},
): Array<Record<string, unknown>> {
  const random = seededRandom(seedFrom(seedKey));
  const span = max - min;
  const rows: Array<Record<string, unknown>> = [];

  for (const category of categories) {
    for (const segment of series ?? [null]) {
      const revenue = Math.round(min + random() * span);
      rows.push({
        [SAMPLE_CATEGORY_FIELD]: category,
        [SAMPLE_VALUE_FIELD]: revenue,
        // Cost tracks revenue at 55–80%, so combo charts look plausible.
        [SAMPLE_SECOND_VALUE_FIELD]: Math.round(revenue * (0.55 + random() * 0.25)),
        ...(segment ? { [SAMPLE_SERIES_FIELD]: segment } : {}),
      });
    }
  }
  return rows;
}

// ── Per-layoutType payloads ──────────────────────────────────────────────────

/**
 * The sample `[data]` object for a layoutType, in the exact envelope that
 * layout's shared component already accepts — identical in shape to what the
 * DVM Layout Builder produces, so a sample widget can later be swapped to real
 * data without changing anything else on the node.
 *
 * Returns `null` for layouts that have no meaningful sample (input controls
 * already render fine with their own defaults).
 */
export function sampleWidgetData(
  layoutType: string,
  nodeId: string,
  title: string,
  appearance: Record<string, unknown> = {},
  variantMeta: VariantMeta = {},
): Record<string, unknown> | null {
  const widgetId = `dsg-${nodeId}`;
  const variant = typeof appearance['variant'] === 'string' ? (appearance['variant'] as string) : '';
  const orientation = appearance['orientation'] === 'horizontal' ? 'horizontal' : 'vertical';

  switch (layoutType) {
    case 'BAR': {
      // A stacked/grouped variant is meaningless without series, and a
      // comparison variant needs a second measure — so the sample data is
      // generated to MATCH the chosen variant. That is what makes picking
      // "Stacked" at design time actually render a stacked chart.
      const needsSeries = usesSeries(variant, variantMeta);
      const twoMeasures = variantMeta.needsTwoMeasures ?? variant === 'comparison';
      const measures = [
        { field: SAMPLE_VALUE_FIELD, label: 'Revenue', renderAs: 'bar', axis: 'y1' },
        ...(twoMeasures
          ? [{ field: SAMPLE_SECOND_VALUE_FIELD, label: 'Cost', renderAs: 'bar', axis: 'y1' }]
          : []),
      ];
      return {
        widgetId,
        title,
        height: '100%',
        capabilities: {
          orientation,
          variant: variant || 'grouped',
          category: SAMPLE_CATEGORY_FIELD,
          ...(needsSeries && !twoMeasures ? { series: SAMPLE_SERIES_FIELD } : {}),
          measures,
          showLegend: true,
        },
        staticData: sampleRows(nodeId, {
          series: needsSeries && !twoMeasures ? SEGMENTS : undefined,
        }),
      };
    }

    case 'LINE_AREA': {
      const multi = /multi|stacked|100/.test(variant);
      return {
        widgetId,
        title,
        height: '100%',
        capabilities: {
          variant: variant || 'line',
          category: SAMPLE_CATEGORY_FIELD,
          ...(multi ? { series: SAMPLE_SERIES_FIELD } : {}),
          measures: [
            {
              field: SAMPLE_VALUE_FIELD,
              label: 'Revenue',
              renderAs: variant.includes('area') ? 'area' : 'line',
              axis: 'y1',
            },
          ],
          showLegend: true,
        },
        staticData: sampleRows(nodeId, {
          categories: MONTHS.slice(0, 8),
          series: multi ? SEGMENTS.slice(0, 2) : undefined,
        }),
      };
    }

    case 'SCATTER_BUBBLE':
      return {
        widgetId,
        title,
        height: '100%',
        capabilities: {
          variant: variant || 'bubble',
          xField: SAMPLE_VALUE_FIELD,
          yField: SAMPLE_SECOND_VALUE_FIELD,
          // Only a bubble variant maps a size field; a plain scatter would
          // otherwise render every point at the same exaggerated radius.
          ...(variant.includes('bubble') ? { sizeField: SAMPLE_VALUE_FIELD } : {}),
          colorByField: SAMPLE_SERIES_FIELD,
          showLegend: true,
        },
        staticData: sampleRows(nodeId, { categories: PRODUCTS, series: SEGMENTS }),
      };

    case 'PIE_COMPOSITION': {
      // Hierarchy variants (Sunburst / Treemap / Nested Donut) read a parent
      // column, so the sample rows are split by segment for them; a progress
      // meter shows ONE value against a target. Prefers the config's own
      // `usesParent` / `gauge` flags and falls back to the variant name.
      const hierarchy = usesParent(variant, variantMeta);
      const gauge = isGauge(variant, variantMeta);
      const random = seededRandom(seedFrom(`${nodeId}:pie`));
      const gaugeValue = Math.round(40000 + random() * 60000);
      return {
        widgetId,
        title,
        height: '100%',
        capabilities: {
          variant: variant || 'pie',
          categoryField: SAMPLE_CATEGORY_FIELD,
          valueField: SAMPLE_VALUE_FIELD,
          ...(hierarchy ? { parentField: SAMPLE_SERIES_FIELD } : {}),
          ...(gauge ? { targetValue: Math.round(gaugeValue * (1.2 + random() * 0.4)) } : {}),
          showLegend: !gauge,
        },
        staticData: gauge
          ? [{ [SAMPLE_CATEGORY_FIELD]: 'Attainment', [SAMPLE_VALUE_FIELD]: gaugeValue }]
          : sampleRows(nodeId, { categories: PRODUCTS.slice(0, 5), series: hierarchy ? SEGMENTS : undefined }),
      };
    }

    case 'TABLE_MATRIX':
      return sampleTableMatrixData(nodeId, title, widgetId, variant || 'normal');

    case 'HORIZONTAL_BAR_CHART': {
      // This component reads fixed `categoryKey` / `valueKey` names.
      const rows = sampleRows(nodeId, { categories: PRODUCTS }).map((row) => ({
        department: row[SAMPLE_CATEGORY_FIELD],
        value: row[SAMPLE_VALUE_FIELD],
      }));
      return {
        widgetId,
        title,
        height: '100%',
        staticData: rows,
        categoryKey: 'department',
        valueKey: 'value',
        fullYAxisLabels: true,
        yAxisLabelWidth: 120,
        valueLabelPosition: 'inside',
        showXAxis: true,
        xAxisName: 'Revenue',
        yAxisName: '',
      };
    }

    case 'KPI': {
      const random = seededRandom(seedFrom(nodeId));
      return {
        title,
        key: 'value',
        widgetId,
        alignment: 'center',
        // No apiUrl ⇒ getConfig returns `data.data` unwrapped, so the value is
        // inlined under the fixed `value` key the component looks up.
        data: { data: [{ value: Math.round(20000 + random() * 120000) }] },
      };
    }

    case 'WATERFALL':
      return {
        type: 'waterfall-chart',
        title,
        widgetId,
        height: '100%',
        hideCardHeader: true,
        waterfallBars: sampleWaterfallBars(nodeId),
      };

    case 'DRILL_DOWN_TABLE':
      return { ...sampleDrillDownData(nodeId, widgetId), title, height: '100%' };

    case 'NESTED_TABLE':
      // Config lives on the node; rows come from the mock-data endpoint, the
      // same way the JSON-driven smart table is wired in all-chart-1.json.
      return { ...sampleNestedTableData(nodeId, appearance), widgetId, title: str(appearance['title'], title) };

    // ── Input controls ────────────────────────────────────────────────────
    // These used to return null and render as empty boxes. They have no
    // dataset either, so they get the same treatment as the charts: a label
    // you can edit now and placeholder options that look like real data.

    case 'DROPDOWN': {
      const label = str(appearance['label'], 'Region');
      return {
        title,
        label,
        name: label,
        key: 'sample_dropdown',
        placeholder: str(appearance['placeholder'], `Select ${label.toLowerCase()}`),
        multiple: appearance['multiple'] === true,
        // Both shapes are accepted by the shared dropdown, so send both rather
        // than betting on one.
        options: SAMPLE_DROPDOWN_OPTIONS,
        data: { data: SAMPLE_DROPDOWN_OPTIONS.map((value) => ({ label: value, value })) },
      };
    }

    case 'SLIDER': {
      const label = str(appearance['label'], 'Discount %');
      const min = numberOr(appearance['min'], 0);
      const max = numberOr(appearance['max'], 100);
      return {
        key: `dsg-${nodeId}`,
        title,
        textCase: 'capital',
        // No apiUrl ⇒ getConfig returns `data.data` unwrapped, so the slider
        // renders entirely from this inline config with no backend.
        data: {
          data: {
            name: label,
            min,
            max,
            step: numberOr(appearance['step'], 1),
            rangeStart: min,
            rangeEnd: max,
          },
        },
      };
    }

    // Everything the zone slider shows is CONFIGURATION (appearance tier —
    // value, range, zones, formats) overlaid by `resolveWidgetData`, or data
    // the binding resolves at render time. So the data object itself is just
    // the identity; it exists so the host renders the control, not the
    // "bind data" placeholder.
    case 'ZONE_SLIDER':
    // Same for the button: label, style and feature are all appearance.
    case 'BUTTON':
      return { widgetId, title };

    case 'INPUT_BOX': {
      const label = str(appearance['label'], 'Target margin');
      return {
        title,
        label,
        placeholder: str(appearance['placeholder'], 'Enter a value'),
      };
    }

    case 'FILE_UPLOAD':
      return {
        title,
        buttonLabel: str(appearance['buttonLabel'], 'Upload file'),
        accept: str(appearance['accept'], '.xlsx,.xls,.csv'),
      };

    default:
      return null;
  }
}

// ── Tables ───────────────────────────────────────────────────────────────────

const TABLE_COLUMNS = [
  { field: SAMPLE_CATEGORY_FIELD, label: 'Region', type: 'text' },
  { field: SAMPLE_SERIES_FIELD, label: 'Segment', type: 'text' },
  { field: SAMPLE_VALUE_FIELD, label: 'Revenue', type: 'number' },
  { field: SAMPLE_SECOND_VALUE_FIELD, label: 'Cost', type: 'number' },
];

/**
 * Table & Matrix renders a different model per table type, and each type
 * reads its OWN capability fields — a flat `columns` list alone leaves every
 * type except Normal on "Map columns to build the table".
 */
function sampleTableMatrixData(
  nodeId: string,
  title: string,
  widgetId: string,
  tableType: string,
): Record<string, unknown> {
  const base = { widgetId, title, height: '100%' };
  const rows = sampleRows(nodeId, { series: SEGMENTS });

  switch (tableType) {
    case 'matrix':
      // Cross-tab: regions down, one revenue column per segment across.
      return {
        ...base,
        capabilities: {
          tableType,
          rowField: SAMPLE_CATEGORY_FIELD,
          columnField: SAMPLE_SERIES_FIELD,
          valueField: SAMPLE_VALUE_FIELD,
          columns: TABLE_COLUMNS,
        },
        staticData: rows,
      };

    case 'editable':
      // Dimensions stay read-only so the sample shows which cells take input.
      return {
        ...base,
        capabilities: {
          tableType,
          columns: TABLE_COLUMNS.map((column) =>
            column.type === 'text' ? { ...column, editable: false } : { ...column, editable: true }),
        },
        staticData: rows,
      };

    case 'drilldown':
      // Delegates to lib-drill-down, which reads a pre-built tree from
      // `drilldownData` — the same payload the standalone Drill-Down Table takes.
      return {
        ...base,
        capabilities: {
          tableType,
          dimensions: [
            { field: SAMPLE_CATEGORY_FIELD, label: 'Region' },
            { field: SAMPLE_SERIES_FIELD, label: 'Segment' },
            { field: 'product', label: 'Product' },
          ],
          measures: [
            { field: SAMPLE_VALUE_FIELD, label: 'Revenue', aggregation: 'sum' },
            { field: SAMPLE_SECOND_VALUE_FIELD, label: 'Cost', aggregation: 'sum' },
          ],
        },
        drilldownData: sampleDrillDownData(nodeId, widgetId),
      };

    default:
      return { ...base, capabilities: { tableType, columns: TABLE_COLUMNS }, staticData: rows };
  }
}

/**
 * lib-drill-down's pre-fetched payload: `staticData: { data, total }` where
 * `data` is a TREE (Region → Segment → Product). Every parent carries the sum
 * of its children, so expanding a row drills into numbers that add up.
 */
export function sampleDrillDownData(nodeId: string, widgetId: string): Record<string, unknown> {
  const random = seededRandom(seedFrom(`${nodeId}:drill`));
  const products = PRODUCTS.slice(0, 3);

  const sum = (nodes: Array<Record<string, unknown>>, field: string): number =>
    nodes.reduce((acc, node) => acc + Number(node[field] ?? 0), 0);
  const parent = (label: string, level: number, children: Array<Record<string, unknown>>) => ({
    label,
    level,
    [SAMPLE_VALUE_FIELD]: sum(children, SAMPLE_VALUE_FIELD),
    [SAMPLE_SECOND_VALUE_FIELD]: sum(children, SAMPLE_SECOND_VALUE_FIELD),
    expanded: false,
    children,
  });

  const tree = REGIONS.map((region) =>
    parent(region, 1, SEGMENTS.map((segment) =>
      parent(segment, 2, products.map((product) => {
        const revenue = Math.round(4000 + random() * 26000);
        return {
          label: product,
          level: 3,
          [SAMPLE_VALUE_FIELD]: revenue,
          [SAMPLE_SECOND_VALUE_FIELD]: Math.round(revenue * (0.55 + random() * 0.25)),
          children: [],
        };
      })),
    )),
  );
  tree.forEach((node, index) => { if (index === 0) node.expanded = true; });

  return {
    widgetId,
    nameProp: 'label',
    columnMode: 'force',
    tableWidth: '100%',
    headers: [
      { name: 'Region / Segment / Product', prop: 'label', expandabile: true, indentByLevel: true, width: 240 },
      { name: 'Revenue', prop: SAMPLE_VALUE_FIELD, type: 'number', width: 130 },
      { name: 'Cost', prop: SAMPLE_SECOND_VALUE_FIELD, type: 'number', width: 130 },
    ],
    staticData: {
      data: tree,
      total: {
        [SAMPLE_VALUE_FIELD]: sum(tree, SAMPLE_VALUE_FIELD),
        [SAMPLE_SECOND_VALUE_FIELD]: sum(tree, SAMPLE_SECOND_VALUE_FIELD),
      },
    },
  };
}

// ── Nested table ─────────────────────────────────────────────────────────────

const CUSTOMERS = ['Komatsu', 'Caterpillar', 'Hitachi', 'Volvo CE', 'Liebherr', 'Doosan', 'JCB', 'Sany', 'Hyundai CE', 'Kubota'];
const ENGINE_MODELS = ['Q38-SS', 'Q60-DS', 'Q50-SS', 'Q78-SS', 'Q45-DS', 'Q90-SS'];
const CERTIFICATIONS = ['TIER 4 FINAL', 'EPA TIER 3', 'STAGE V', 'TIER 4 FINAL'];
const APPLICATIONS = ['Haul Truck', 'Excavator', 'Wheel Loader', 'Dozer', 'Grader', 'Crusher'];
const OEM_POSITIONS = ['MFN', 'Below MFN', 'Above MFN'];
const PLANNING_STATUSES = ['Planned', 'In review', 'Approved', 'On hold'];
const JUSTIFICATIONS = ['Market alignment', 'Cost increase', 'Volume commitment', 'Competitive pressure', 'Strategic account'];

/**
 * Dummy rows for the nested table — generated, seeded per node, so the same
 * design always shows the same numbers. There are NO hard-coded columns: the
 * table derives its headers from these row keys, and the config only layers
 * formatting (currency, badge, editors…) on top through `columnOverrides`.
 * Change the generated keys and the headers follow.
 */
export function sampleNestedTableData(
  nodeId: string,
  appearance: Record<string, unknown> = {},
): Record<string, unknown> {
  const random = seededRandom(seedFrom(`${nodeId}:nested`));
  const pick = <T>(list: readonly T[]): T => list[Math.floor(random() * list.length)];
  const between = (min: number, max: number): number => Math.round(min + random() * (max - min));
  const clampInt = (value: unknown, fallback: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, Math.round(numberOr(value, fallback))));

  const levelCount = clampInt(appearance['levelCount'], 3, 1, 3);
  const customerCount = clampInt(appearance['rowCount'], 4, 1, CUSTOMERS.length);
  const childCount = clampInt(appearance['childRowCount'], 3, 1, 6);

  const spec = (customer: string, index: number): Record<string, unknown> => {
    const sales25 = between(300, 1800);
    const currentPrice = random() < 0.15 ? null : between(7000, 14000);
    return {
      custSpec: `${customer.slice(0, 2).toUpperCase()}-${3800 + index}${String.fromCharCode(65 + index)}`,
      application: pick(APPLICATIONS),
      sales25,
      sales26: Math.round(sales25 * (1.05 + random() * 0.2)),
      volume25: between(200, 1200),
      volume26: between(220, 1300),
      margin25: between(14, 24),
      margin26: between(15, 26),
      currentPrice,
      targetPrice: currentPrice === null ? null : Math.round(currentPrice * (1.01 + random() * 0.03)),
      setPrice: currentPrice === null ? null : Math.round(currentPrice * 1.02),
      priceJustification: random() < 0.5 ? null : pick(JUSTIFICATIONS),
      planningStatus: pick(PLANNING_STATUSES),
      comment: '',
    };
  };

  const segment = (customer: string, index: number): Record<string, unknown> => {
    const specs = Array.from({ length: childCount }, (_, i) => spec(customer, index * 10 + i));
    const sum = (field: string) => specs.reduce((total, row) => total + Number(row[field] ?? 0), 0);
    return {
      customerName: customer,
      engineModel: `${ENGINE_MODELS[index % ENGINE_MODELS.length]} / ${pick(CERTIFICATIONS)}`,
      sales2025: sum('sales25'),
      sales2026: sum('sales26'),
      margin2025: between(15, 22),
      margin2026: between(16, 24),
      volume2025: sum('volume25'),
      volume2026: sum('volume26'),
      ...(levelCount >= 3 ? { specs, expanded: index === 0 } : {}),
    };
  };

  const rows = CUSTOMERS.slice(0, customerCount).map((customer, index) => {
    const segments = Array.from({ length: childCount }, (_, i) => segment(customer, i));
    const sum = (field: string) => segments.reduce((total, row) => total + Number(row[field] ?? 0), 0);
    const position = index === 0 ? 'MFN' : pick(OEM_POSITIONS);
    const delta = position === 'MFN' ? null : Math.round((random() * 6 - 3) * 10) / 10;
    return {
      sNo: index + 1,
      customer,
      sales2025: sum('sales2025') * 4,
      sales2026: sum('sales2026') * 4,
      margin2025: between(24, 34),
      margin2026: between(25, 36),
      volume2025: sum('volume2025') * 2,
      volume2026: sum('volume2026') * 2,
      oemPosition: position,
      proposedDelta: delta,
      plannedDelta: delta === null ? null : Math.round(delta * 0.8 * 10) / 10,
      planningStatus: position === 'MFN' ? null : pick(PLANNING_STATUSES),
      ...(levelCount >= 2 ? { segments, expanded: index === 0 } : {}),
    };
  });

  const money = { type: 'currency', suffix: 'K', align: 'right' };
  const pct = { type: 'percent', align: 'right' };
  const levels: Array<Record<string, unknown>> = [
    {
      childrenField: 'segments',
      columnOverrides: {
        sNo: { label: 'S No', type: 'index', width: 48 },
        customer: { bold: true },
        sales2025: { ...money, mono: true },
        sales2026: { ...money, mono: true },
        margin2025: { ...pct, mono: true },
        margin2026: { ...pct, mono: true },
        volume2025: { mono: true },
        volume2026: { mono: true },
        oemPosition: {
          label: 'OEM Position', type: 'badge', align: 'center',
          badgeStyles: {
            'Below MFN': { background: '#fef3f2', color: '#b42318' },
            'Above MFN': { background: '#ecfdf3', color: '#027a48' },
          },
        },
        proposedDelta: { label: 'Proposed Delta wrt MFN', ...pct, decimals: 1, align: 'center', emptyText: '-' },
        plannedDelta: { label: 'Planned Delta wrt MFN', ...pct, decimals: 1, align: 'center', emptyText: '-' },
        planningStatus: { align: 'center', emptyText: '-' },
      },
    },
    {
      title: 'Segment Level Breakdown',
      childrenField: 'specs',
      columnOverrides: {
        customerName: { bold: true },
        engineModel: { label: 'Engine Model+TS / Certification', mono: true },
        sales2025: { ...money, bold: true },
        sales2026: { ...money, bold: true },
        margin2025: { ...pct, color: '#4a8fe7' },
        margin2026: { ...pct, color: '#4a8fe7' },
        volume2025: { bold: true },
        volume2026: { bold: true },
      },
    },
    {
      title: 'Spec Level Breakdown',
      dense: true,
      actions: [
        { id: 'export', label: 'Export' },
        { id: 'planning-view', label: 'Planning View' },
      ],
      columnOverrides: {
        custSpec: { bold: true },
        sales25: { type: 'currency', suffix: 'K', label: "Sales '25" },
        sales26: { type: 'currency', suffix: 'K', label: "Sales '26" },
        volume25: { label: "Volume '25", align: 'left' },
        volume26: { label: "Volume '26", align: 'left' },
        margin25: { type: 'percent', label: "Margin '25" },
        margin26: {
          type: 'percent', label: "Margin '26",
          colorRules: [
            { op: 'gte', value: 24, color: '#027a48', bold: true },
            { op: 'lt', value: 18, color: '#b42318' },
          ],
        },
        currentPrice: { type: 'currency' },
        targetPrice: { type: 'currency', color: '#98a2b3' },
        setPrice: { type: 'input', prefix: '$', inputType: 'number', placeholder: '—', width: 84 },
        priceJustification: { type: 'dropdown', placeholder: 'Select...', options: JUSTIFICATIONS, width: 124 },
        planningStatus: { type: 'dropdown', options: PLANNING_STATUSES, width: 104 },
        comment: { type: 'input', inputType: 'text', required: true, placeholder: 'Comment (required)', width: 150 },
      },
    },
  ].slice(0, levelCount);

  return {
    subtitle: str(appearance['subtitle'], 'Inspect relative positions against MFN reference and input scenario deltas'),
    emptyText: '—',
    levels,
    staticData: rows,
  };
}

/** Variants that plot ONE bar per category — no series split, no second measure. */
const SINGLE_SERIES_VARIANTS = new Set(['simple', 'comparison']);

/**
 * Whether this variant needs a series split in the sample data.
 *
 * Prefers the config's own `usesSeries` flag, but falls back to the variant
 * NAME when the flag is absent. Without the fallback an unresolved lookup
 * defaulted to "yes", so picking **Simple Bar** still generated series rows and
 * the chart kept rendering grouped — the variant looked like it did nothing.
 */
export function usesSeries(variant: string, meta: VariantMeta = {}): boolean {
  if (typeof meta.usesSeries === 'boolean') return meta.usesSeries;
  return !SINGLE_SERIES_VARIANTS.has(variant);
}

/** Pie & Composition variants that need a parent column (config flag first, name fallback). */
export function usesParent(variant: string, meta: VariantMeta = {}): boolean {
  if (typeof meta.usesParent === 'boolean') return meta.usesParent;
  return /sunburst|treemap|nested/.test(variant);
}

/** Pie & Composition single-value progress meters (config flag first, name fallback). */
export function isGauge(variant: string, meta: VariantMeta = {}): boolean {
  if (typeof meta.gauge === 'boolean') return meta.gauge;
  return /progress|gauge/.test(variant);
}

/** Placeholder dropdown choices — recognisably sample, not fake-real values. */
export const SAMPLE_DROPDOWN_OPTIONS = ['All', 'North', 'South', 'East', 'West'];

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function numberOr(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** A running total that dips and recovers — the shape a bridge chart is for. */
function sampleWaterfallBars(nodeId: string): Array<Record<string, unknown>> {
  const random = seededRandom(seedFrom(nodeId));
  const steps: Array<{ label: string; delta: number; type: string }> = [
    { label: 'Opening', delta: Math.round(80000 + random() * 40000), type: 'START' },
    { label: 'New business', delta: Math.round(15000 + random() * 20000), type: 'CHANGE' },
    { label: 'Upsell', delta: Math.round(6000 + random() * 12000), type: 'CHANGE' },
    { label: 'Churn', delta: -Math.round(8000 + random() * 14000), type: 'CHANGE' },
    { label: 'Discounts', delta: -Math.round(3000 + random() * 7000), type: 'CHANGE' },
  ];

  const bars: Array<Record<string, unknown>> = [];
  let running = 0;
  for (const step of steps) {
    const bottom = step.type === 'START' ? 0 : running;
    running = bottom + step.delta;
    bars.push({
      label: step.label,
      delta: step.delta,
      bottom: Math.min(bottom, running),
      top: Math.max(bottom, running),
      type: step.type,
      colorSign: step.delta >= 0 ? 'POSITIVE' : 'NEGATIVE',
    });
  }
  // Closing total spans from zero, so it reads as an absolute, not a delta.
  bars.push({ label: 'Closing', delta: running, bottom: 0, top: running, type: 'TOTAL' });
  return bars;
}

/**
 * The bits of the chosen variant the generator needs. Comes from the builder
 * config's variant list (`barVariants` et al), which already declares them.
 */
export interface VariantMeta {
  usesSeries?: boolean;
  stack?: boolean;
  normalize100?: boolean;
  needsTwoMeasures?: boolean;
  /** Pie & Composition: the variant reads a parent / outer-group column. */
  usesParent?: boolean;
  /** Pie & Composition: a single-value progress meter. */
  gauge?: boolean;
}

/** Attach sample data to a widget, marking it as sample. */
export function withSampleData(
  widget: WidgetContent,
  nodeId: string,
  title: string,
  variantMeta: VariantMeta = {},
): WidgetContent {
  const data = sampleWidgetData(
    widget.layoutType,
    nodeId,
    title,
    widget.appearance ?? {},
    variantMeta,
  );
  return data ? { ...widget, data, sample: !CONFIG_ONLY_LAYOUT_TYPES.has(widget.layoutType) } : widget;
}

/**
 * Widgets whose `[data]` is configuration rather than generated rows: nothing
 * about them is "sample" that publishing would have to strip, so they are never
 * flagged as sample data (and never counted as unbound by the shell).
 */
export const CONFIG_ONLY_LAYOUT_TYPES: ReadonlySet<string> = new Set(['ZONE_SLIDER', 'BUTTON']);
